<template>
  <div id="ConfirmQuestion" class="border-brand-dark">
    <div
      class="dmh-confirm-list w-100"
      v-for="(question, i) in question.options"
      :key="i"
      :class="{
        invalid:
          touched_fields.includes(question.field_key) &&
          !validations[question.field_key]
      }"
    >
      <CustomSelect
        v-if="question.field_type === 'custom_select'"
        :question="question"
        :answer="answer"
      />
      <div 
      v-else
        class="dmh-confirm-container w-100 mb-0"
        :class="{
          'read-only-container':
            question.attributes && question.attributes.includes('readonly')
        }"
      >
        <label class="dmh-confirm-field">{{ question.field_label }}</label>
        <select
          v-if="isAddressSelectShown(question)"
          class="dmh-confirm-input"
          v-model="answer[question.field_key]"
          @focus="markAsTouched(question.field_key)"
          @change="handleInputChange(answer[question.field_key])"
        >
          <option
            v-for="(option, index) in postcodeResults"
            :key="index"
            :value="option"
            >{{ option }}</option
          >
          <option
            v-if="(question.field_key && question.field_key.includes('address'))"
            value="manual-address"
          >{{ "other" }}</option
          >
        </select>
        <select
          @focus="markAsTouched(question.field_key)"
          class="dmh-confirm-input"
          :class="{ empty: answer[question.field_key] === '' }"
          v-model="answer[question.field_key]"
          v-else-if="question.field_type === 'select'"
        >
          <option disabled value="" hidden>Please select one</option>
          <option
            v-for="(option, index) in question.options"
            :key="index"
            :value="option"
            >{{ option }}</option
          >
        </select>

        <input
          @input="handleInput($event, question)"
          :value="question.data ? question.data : ''"
          v-model="answer[question.field_key]"
          :type="question.field_type"
          :readonly="
            question.attributes && question.attributes.includes('readonly')
          "
          class="dmh-confirm-input"
          :max="question.field_type === 'date' ? today : null"
          :class="{
            'read-only-field':
              question.attributes && question.attributes.includes('readonly')
          }"
          v-else
        />
      </div>

      <div
        v-if="
          touched_fields.includes(question.field_key) &&
            getErrorMessages(question).length
        "
      >
        <div
          v-for="(error, i) in getErrorMessages(question)"
          :key="i"
          class="error-text d-flex flex-row align-items-center"
        >
          <i class="fa-exclamation-circle fas mr-3"></i>
          <div>{{ error }}</div>
        </div>
      </div>
    </div>
    <p
      v-if="question.sub_text_html || question.sub_text"
      v-html="decodeData(question.sub_text_html || question.sub_text)"
      class="sub_text"
    ></p>
    <p v-if="question.footer_text" class="footer-text">
      {{ question.footer_text }}
    </p>
  </div>
</template>

<script>
import CustomSelect from "./CustomSelect.vue";
import journeyMixin from "../../mixins/journeyMixin";

export default {
  name: "ConfirmQuestion",
  mixins: [journeyMixin],
  props: ["question", "answer", "setAnswer", "userId", "apiQuestionnaire", "apiUrl"],
  components: {
    CustomSelect
  },
  data() {
    return {
      touched_fields: [],
      postcodeResults: [],
      debouncedFetch: null,
      isAddressLoading: false,
      isAddressEditable: false,
    };
  },
  created() {
    if (Array.isArray(this.question?.options)) {
      this.question.options.forEach((field) => {
        if (field.field_key?.includes("dob") && field.validation) {
          if (!this.touched_fields.includes(field.field_key)) {
            this.touched_fields.push(field.field_key);
          }
        }
      });
    }
  },
  computed: {
    validations() {
      return this.validateDmhAnswers(this.answer, this.question, this.apiUrl);
    },
    isAuApi() {
      return this.isAustralianApiUrl(this.apiUrl);
    },
    today() {
      const date = new Date();
      return date.toISOString().split('T')[0];
    },
    postCodeValue() {
      if (!this.answer) return '';

      const postCodeKey = Object.keys(this.answer).find(key => key.includes('post_code'));
      return postCodeKey ? this.answer[postCodeKey] : '';
    }
  },
  methods: {
    decodeData(data) {
      try {
        return atob(data);
      } catch (error) {
        return data;
      }
    },
    markAsTouched(fieldKey) {
      if (!this.touched_fields.includes(fieldKey)) {
        this.touched_fields.push(fieldKey);
      }
    },
    wasAddressPreviouslyAnswered(key) {
      if (this.answer && Object.hasOwn(this.answer, key)) {
        if (this.answer[key] != "") {
          return true;
        }
      }
      return false;
    },
    isAddressSelectShown(question) {
      if (this.isAuApi) return false;
      return question?.field_key && question?.field_key.includes('address') &&
              question.action &&
              question.action.type === 'postcode_exchange' &&
              this.postCodeValue &&
              !this.isAddressEditable &&
              !this.wasAddressPreviouslyAnswered(question.field_key)
    },
    getErrorMessages(question) {
      const fieldKey = question.field_key;
      const errors = [];
      if (!this.validations[fieldKey]) {
        if (fieldKey.includes("email"))
          errors.push("Must be a valid email address");
        else if (fieldKey.includes("phone_number"))
          errors.push("Must be a valid phone number with at least 7 digits");
        else if (fieldKey.includes("dob") && question.validation)
          errors.push(this.DOBValidationMessage(question));
        else if (
          !this.isAuApi &&
          fieldKey.includes("address") && this.postCodeValue && !this.postcodeResults.length && !this.isAddressLoading
        )
          errors.push("Postcode not found");
        else if (fieldKey === "address")
          errors.push("Must be at least 10 characters");
        else if (fieldKey.includes("post_code"))
          errors.push("Must be a valid UK postcode");
        else errors.push("Must not be empty");
      }
      return errors;
    },
    
    DOBValidationMessage(question) {
      const validation = question.validation || {};
      const validationMaxDefault = 130

      if (validation.min) {
        return `Must be between ${validation.min} and ${validation.max || validationMaxDefault} years old`;
      } else {
        return `Must be at most ${validation.max || validationMaxDefault} years old`;
      }
    },
    async fetchPostcodeAddress(post_code) {
      if (this.isAuApi) return;
      this.isAddressEditable = false;
      this.postcodeResults = []
      const cleanedPostcode = post_code.replace(/\s+/g, "");
      if (!cleanedPostcode || cleanedPostcode.length < 5) return;
      const payload = {
        user_id: this.userId,
        post_code: cleanedPostcode
      };
      try {
        this.isAddressLoading = true;
        const res = await this.apiQuestionnaire.getPostcodeAddress(payload);
        if (res.data.message && res.data.message === "Postcode not found") {
          this.postcodeResults = [];
        } else {
          this.postcodeResults =
            res.data?.suggestions?.map(el => el.address) || [];
        }
      } catch (error) {
        console.error("Error fetching postcode addresses", error);
      } finally {
        this.isAddressLoading = false;
      }
    },
    handlePostcodeExchange(question, postCodeValue) {
      if (this.isAuApi) return;
      if (
        question.field_key?.includes("post_code") &&
        question.action &&
        question.action.type === "postcode_exchange"
      ) {
        const addressKey = Object.keys(this.answer).find(key =>
          key.includes("address")
        );
        this.answer[addressKey] = "";

        if (!this.debouncedFetch) {
          this.debouncedFetch = this.debounceInput(
            this.fetchPostcodeAddress,
            500
          );
        }

        this.debouncedFetch(postCodeValue);
      }
    },
    handleInput(event, question) {
      if (question.field_type !== 'date') {
        const cleanValue = event.target.value.replace(/[^a-zA-Z0-9 .,@+]/g, '');
        this.answer[question.field_key] = cleanValue;
      }

      this.markAsTouched(question.field_key);
      this.handlePostcodeExchange(question, this.answer[question.field_key]);
    },
    handleInputChange(value, fieldKey) {
      if (value === "manual-address") {
        Object.keys(this.answer).forEach(key => {
          if (key.includes("address") && key !== fieldKey) {
            this.answer[key] = "";
          }
        });

        this.isAddressEditable = true;
      }
    }
  },
  watch: {
    answer: {
      handler(newVal) {
        this.handlePostcodeExchange(
          this.question,
          newVal[this.question.field_key]
        );
      },
      deep: true
    }
  }
};
</script>

<style lang="less">
#ConfirmQuestion {
  display: flex;
  flex-direction: column;
  align-items: center;
  accent-color: var(--brand-primary);

  .sub_text {
    color: #122130;
    letter-spacing: normal;
    font-size: 1.6rem;
    margin-top: 28px;
    h3 {
      letter-spacing: normal;
      font-size: 1.6rem;
      color: #122130;
      font-family: var(--font-bold, "DMSans Bold");
      margin: 0 0 20px;
      padding: 0;
    }
    p,
    li {
      letter-spacing: normal;
      font-size: 1.6rem;
      color: #122130;
      font-family: var(--font-regular, "DMSans");
      padding: 0;
    }
    p {
      margin: 0 0 20px;
    }
    li {
      margin: 20px 0 0 8px;
    }
  }

  .dmh-confirm-list:not(:first-child) {
    margin-top: 24px;
  }

  .dmh-confirm-field {
    font-family: var(--font-bold, "DMSans Bold");
    color: #797b89;
    font-size: 1.4rem;
  }

  .dmh-confirm-container {
    background-color: white;
    display: flex;
    flex-direction: column;
    font-size: 1.8rem;
    border-radius: 8px;
    border: 1px solid #aeb0bd;
    padding: 15px;

    .dmh-confirm-input {
      background-color: transparent;
      color: #1e1e22;
      font-family: var(--font-regular, "DMSans");
      font-size: 1.6rem;
      accent-color: var(--brand-primary);
    }

    .read-only-field {
      background-color: #f4f6fc;
      color: #797b89;
      cursor: not-allowed;
    }

    input {
      border: none;
      width: 100%;
      accent-color: var(--brand-primary);
    }

    input:focus {
      outline-width: 0;
      accent-color: var(--brand-primary);
    }

    select {
      background-color: white;
      cursor: pointer;
      border: none;
      width: 100%;
    }

    select:focus {
      outline-width: 0;
    }

    &.invalid {
      border-color: #992f0c !important;
    }
  }

  .read-only-container {
    background-color: #f4f6fc !important;
    color: #797b89;
    border: solid 1px #F4F6FC;

      .dmh-confirm-input {
        color: #797b89;
      }
  }

  select.empty {
    color: #8e8e90 !important;
  }

  .dmh-confirm-container:focus-within {
    border-color: #1e1e22;
  }
  .error-text {
    border-radius: 8px;
    border: 1px solid #f40b0b;
    color: #f40b0b;
    background-color: #eababa;
    padding: 10px;
    margin-top: 10px;
    font-size: 1.6rem;
  }
  a {
    color: #3c16f9;
    text-decoration: underline;
    font-family: var(--font-bold, "DMSans Bold");
  }
  .footer-text {
    font-family: var(--font-regular, "DMSans");
    text-align: center;
    font-size: 1.5rem;
    max-width: 342px;
    margin: 0 auto;
    color: #1E1E22;
    margin-top: 32px;
  }
}
</style>
